import { useCallback, useEffect, useRef } from "react";
import { type QueryHookOptions, useApolloClient } from "@apollo/client";
import {
  useVaultDocumentsSearchQuery,
  VaultDocumentsSearchDocument,
} from "#graphql/supergraphHooks";
import {
  type VaultDocumentsInput,
  type VaultDocumentsSearchQuery,
  type VaultDocumentsSearchQueryVariables,
} from "#graphql/supergraphTypes";

/** A single document node as returned by the `vaultDocumentsSearch` query. */
export type VaultDocumentNode =
  VaultDocumentsSearchQuery["vaultDocumentsSearch"]["nodes"][number];

/** Forward-cursor page info for `vaultDocumentsSearch`. */
export type VaultDocumentPageInfo =
  VaultDocumentsSearchQuery["vaultDocumentsSearch"]["pageInfo"];

export interface UseVaultDocumentsSearchReturn {
  /** The current page of document nodes (server-shaped). */
  nodes: VaultDocumentNode[];
  /** Forward-cursor page info (`hasNextPage` / `endCursor`), or null before load. */
  pageInfo: VaultDocumentPageInfo | null;
  /** Total documents matching the filter, across all pages. */
  totalCount: number;
  loading: boolean;
  error: ReturnType<typeof useVaultDocumentsSearchQuery>["error"];
  refetch: ReturnType<typeof useVaultDocumentsSearchQuery>["refetch"];
  /**
   * Fetch the next cursor page and ADVANCE the internal cursor, so repeated
   * calls walk pages 2, 3, 4… (not re-fetch page 2). Resolves to the fetched
   * page's nodes, or `[]` when there is no next page. The caller accumulates the
   * pages (the backend is forward-cursor only — no random access / offset). The
   * cursor resets whenever the base query re-runs (new filter / refetch).
   */
  fetchNextPage: () => Promise<VaultDocumentNode[]>;
}

/**
 * DV-330 / DV-248 — list/table view of Vault documents across global attributes,
 * filtered across spaces and domains.
 *
 * Thin wrapper over the generated `vaultDocumentsSearch` query, matching the
 * Space/Attribute hook style: `no-cache` fetch (these hooks don't wire cache
 * updates yet), and the connection destructured into `nodes` / `pageInfo` /
 * `totalCount`. `fetchNextPage` advances the forward cursor.
 *
 * For the AG Grid server-side row model, prefer `VaultDocumentsGraphqlDataSource`
 * (it calls the query directly per block); this hook is for simpler consumers
 * that render a mapped page of documents.
 *
 * @example
 * const { nodes, totalCount, fetchNextPage } = useVaultDocumentsSearch({
 *   spaceIds: ["space-1"],
 *   first: 50,
 * });
 */
export const useVaultDocumentsSearch = (
  input: VaultDocumentsInput,
  options?: Omit<
    QueryHookOptions<VaultDocumentsSearchQuery, VaultDocumentsSearchQueryVariables>,
    "variables"
  >,
): UseVaultDocumentsSearchReturn => {
  const client = useApolloClient();
  const { data, loading, error, refetch } = useVaultDocumentsSearchQuery({
    fetchPolicy: "no-cache",
    ...options,
    variables: { input },
  });

  const connection = data?.vaultDocumentsSearch;

  // Advancing forward cursor for `fetchNextPage`. Seeded from the base query's
  // page-1 result and updated after each fetched page (so calls page 2 → 3 → 4),
  // and re-seeded whenever the base query re-runs (new filter / refetch) — the
  // `connection` reference only changes when the data changes.
  const pageStateRef = useRef<{ cursor: string | null; hasNext: boolean }>({
    cursor: null,
    hasNext: false,
  });
  useEffect(() => {
    pageStateRef.current = {
      cursor: connection?.pageInfo.endCursor ?? null,
      hasNext: connection?.pageInfo.hasNextPage ?? false,
    };
  }, [connection]);

  const fetchNextPage = useCallback(async (): Promise<VaultDocumentNode[]> => {
    const { cursor, hasNext } = pageStateRef.current;
    if (!hasNext || !cursor) return [];
    // The connection is forward-cursor only and these hooks read `no-cache`
    // (which rules out Apollo's `fetchMore`), so page forward with a direct
    // query. Callers accumulate the returned nodes themselves.
    const result = await client.query<
      VaultDocumentsSearchQuery,
      VaultDocumentsSearchQueryVariables
    >({
      query: VaultDocumentsSearchDocument,
      variables: { input: { ...input, after: cursor } },
      fetchPolicy: "no-cache",
    });
    const nextConnection = result.data?.vaultDocumentsSearch;
    pageStateRef.current = {
      cursor: nextConnection?.pageInfo.endCursor ?? null,
      hasNext: nextConnection?.pageInfo.hasNextPage ?? false,
    };
    return nextConnection?.nodes ?? [];
  }, [client, input]);

  return {
    nodes: connection?.nodes ?? [],
    pageInfo: connection?.pageInfo ?? null,
    totalCount: connection?.totalCount ?? 0,
    loading,
    error,
    refetch,
    fetchNextPage,
  };
};
